import { getOkrsekTail, getResultDisplayName } from "./Map";
import { parseElectionSortKey, sortElectionsNewestFirst } from "./electionOrdering";
import { parseAppElectionId, supportsTrendsModule } from "./electionBehavior";

export const TRENDS_FAMILY_CONFIG = {
  psp: {
    key: "psp",
    label: "Volby do PSP",
    years: [2017, 2021, 2025],
  },
  kv_default: {
    key: "kv_default",
    label: "Komunální volby (městská část / obvod)",
    years: [2014, 2018, 2022],
  },
  kv_typ1: {
    key: "kv_typ1",
    label: "Komunální volby (město / obec)",
    years: [2014, 2018, 2022],
  },
};

export const TRENDS_FAMILY_ORDER = ["psp", "kv_default", "kv_typ1"];

export const TRENDS_METRICS = [
  { key: "eligible_voters", label: "Počet oprávněných voličů" },
  { key: "participating_voters", label: "Počet hlasujících" },
  { key: "turnout_pct", label: "Volební účast" },
];

export const TRENDS_SORT_OPTIONS = [
  { key: "district_code", label: "Kód okrsku" },
  { key: "biggest_increase", label: "Největší nárůst" },
  { key: "biggest_decrease", label: "Největší pokles" },
  { key: "biggest_absolute_change", label: "Největší absolutní změna" },
];

const PARTY_ANALYSIS_MODE = "party";
const METRIC_ANALYSIS_MODE = "metric";

function toYearNumber(value) {
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function sortYearsChronologically(years) {
  return years.slice().sort((a, b) => a - b);
}

export function parseElectionIdForTrends(electionId) {
  const raw = String(electionId || "").trim();
  const match = raw.match(/^([a-z]+)(\d{4})_(.+)$/i);
  if (!match) return null;
  const sortKey = parseElectionSortKey(raw);
  const parsedAppId = parseAppElectionId(raw);
  return {
    election_key: parsedAppId.election_key,
    family: parsedAppId.family || String(match[1] || "").toLowerCase(),
    year: toYearNumber(match[2]),
    areaSlug: String(match[3] || ""),
    isTyp1: sortKey.kvVariantRank === 1,
    trendsFamily: getTrendsFamilyForElection(raw),
  };
}

export function getTrendsFamilyForElection(election) {
  const sortKey = parseElectionSortKey(election);
  if (!Number.isInteger(sortKey.year)) return null;
  if (sortKey.type === "psp") return "psp";
  if (sortKey.type === "kv") return sortKey.kvVariantRank === 1 ? "kv_typ1" : "kv_default";
  return null;
}

export function createEmptyTrendsElectionMatrix() {
  return TRENDS_FAMILY_ORDER.reduce((matrix, key) => {
    matrix[key] = [];
    return matrix;
  }, {});
}

export function isAllowedTrendsElection(election) {
  const id = String(election && election.id ? election.id : "").trim();
  const parsed = parseElectionIdForTrends(id);
  if (!parsed) return false;
  if (!supportsTrendsModule(parsed.election_key || id.split("_")[0])) return false;
  const family = getTrendsFamilyForElection(election);
  if (!family || !Object.prototype.hasOwnProperty.call(TRENDS_FAMILY_CONFIG, family)) return false;
  if (!TRENDS_FAMILY_CONFIG[family].years.includes(parsed.year)) return false;
  return true;
}

export function buildTrendsElectionMatrix(elections) {
  const byFamily = TRENDS_FAMILY_ORDER.reduce((matrix, key) => {
    matrix[key] = new Map();
    return matrix;
  }, {});

  sortElectionsNewestFirst(elections).forEach((election) => {
    if (!isAllowedTrendsElection(election)) return;
    const parsed = parseElectionIdForTrends(election.id);
    if (!parsed) return;
    const family = getTrendsFamilyForElection(election);
    if (!family || !byFamily[family]) return;
    if (!byFamily[family].has(parsed.year)) {
      byFamily[family].set(parsed.year, election);
    }
  });

  return TRENDS_FAMILY_ORDER.reduce((matrix, family) => {
    matrix[family] = TRENDS_FAMILY_CONFIG[family].years
      .map((year) => ({ year, election: byFamily[family].get(year) || null }))
      .filter((item) => Boolean(item.election));
    return matrix;
  }, {});
}

export function getDefaultTrendsFamily(matrix) {
  return (
    TRENDS_FAMILY_ORDER.find((key) => Array.isArray(matrix && matrix[key]) && matrix[key].length >= 2) ||
    TRENDS_FAMILY_ORDER.find((key) => Array.isArray(matrix && matrix[key]) && matrix[key].length > 0) ||
    null
  );
}

export function buildDefaultDraftQuery(matrix) {
  const family = getDefaultTrendsFamily(matrix);
  if (!family) {
    return {
      family: TRENDS_FAMILY_ORDER[0],
      selectedYears: [],
      analysisMode: PARTY_ANALYSIS_MODE,
      valueMode: "percentage",
      metricKey: TRENDS_METRICS[0].key,
      entitySelections: {},
    };
  }

  const selectedYears = matrix[family].map((item) => item.year);
  return {
    family,
    selectedYears,
    analysisMode: PARTY_ANALYSIS_MODE,
    valueMode: family === "psp" ? "percentage" : "percentage",
    metricKey: TRENDS_METRICS[0].key,
    entitySelections: {},
  };
}

export function getElectionByFamilyYear(matrix, family, year) {
  const list = Array.isArray(matrix && matrix[family]) ? matrix[family] : [];
  const hit = list.find((item) => Number(item.year) === Number(year));
  return hit ? hit.election : null;
}

export function buildAppliedQueryFromDraft(draftQuery, matrix) {
  const family = String(draftQuery && draftQuery.family ? draftQuery.family : "");
  const selectedYears = sortYearsChronologically((Array.isArray(draftQuery && draftQuery.selectedYears) ? draftQuery.selectedYears : [])
    .map((year) => toYearNumber(year))
    .filter((year) => year !== null));

  const years = selectedYears
    .map((year) => ({
      year,
      election: getElectionByFamilyYear(matrix, family, year),
    }))
    .filter((item) => Boolean(item.election));

  return {
    family,
    years,
    analysisMode:
      draftQuery && draftQuery.analysisMode === METRIC_ANALYSIS_MODE
        ? METRIC_ANALYSIS_MODE
        : PARTY_ANALYSIS_MODE,
    valueMode: draftQuery && draftQuery.valueMode === "absolute" ? "absolute" : "percentage",
    metricKey:
      draftQuery && typeof draftQuery.metricKey === "string" && draftQuery.metricKey.trim()
        ? draftQuery.metricKey.trim()
        : TRENDS_METRICS[0].key,
    entitySelections:
      draftQuery && draftQuery.entitySelections && typeof draftQuery.entitySelections === "object"
        ? draftQuery.entitySelections
        : {},
  };
}

export function getEntityOptionsForBundle(bundle) {
  const byKey = new Map();
  const results = Array.isArray(bundle && bundle.results) ? bundle.results : [];
  results.forEach((row) => {
    const list = Array.isArray(row && row.results) ? row.results : [];
    list.forEach((result) => {
      const identity = String(
        result && (result.party_code ?? result.candidate_code ?? getResultDisplayName(result))
      ).trim();
      const label = String(getResultDisplayName(result) || "").trim();
      if (!identity || !label || byKey.has(identity)) return;
      byKey.set(identity, { key: identity, label });
    });
  });
  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label, "cs"));
}

function makeResultsMap(bundle) {
  const map = new Map();
  (Array.isArray(bundle && bundle.results) ? bundle.results : []).forEach((row) => {
    const okrsek = String(row && row.okrsek ? row.okrsek : "").trim();
    if (!okrsek) return;
    map.set(okrsek, row);
  });
  return map;
}

function makeStatsMap(bundle) {
  const map = new Map();
  (Array.isArray(bundle && bundle.stats) ? bundle.stats : []).forEach((row) => {
    const okrsek = String(row && row.okrsek ? row.okrsek : "").trim();
    if (!okrsek) return;
    map.set(okrsek, row);
  });
  return map;
}

function getTotalResultVotes(resultRow) {
  if (!resultRow || !Array.isArray(resultRow.results)) return null;
  return resultRow.results.reduce((sum, item) => {
    const votes = Number(item && item.votes);
    return Number.isFinite(votes) ? sum + votes : sum;
  }, 0);
}

function getSelectedVotes(resultRow, selectedEntities) {
  if (!resultRow || !Array.isArray(resultRow.results)) return null;
  const selectedSet = new Set(
    (Array.isArray(selectedEntities) ? selectedEntities : [])
      .map((item) => String(item && item.key ? item.key : item).trim())
      .filter(Boolean)
  );
  if (selectedSet.size === 0) return null;
  let foundAny = false;
  const total = resultRow.results.reduce((sum, item) => {
    const key = String(
      item && (item.party_code ?? item.candidate_code ?? getResultDisplayName(item))
    ).trim();
    if (!selectedSet.has(key)) return sum;
    foundAny = true;
    const votes = Number(item && item.votes);
    return Number.isFinite(votes) ? sum + votes : sum;
  }, 0);
  return foundAny ? total : 0;
}

function getMetricNumeratorAndDenominator(statsRow, metricKey) {
  if (!statsRow) return { available: false, numerator: null, denominator: null, value: null };
  const eligible = Number(statsRow.VOL_SEZNAM ?? statsRow.vol_seznam);
  const participating = Number(
    statsRow.ODEVZ_OBALEK ??
      statsRow.odevz_obalek ??
      statsRow.ODEVZ_OBAL ??
      statsRow.odevz_obal
  );
  const turnout = Number(statsRow.UCAST_PCT ?? statsRow.ucast_pct);

  if (metricKey === "eligible_voters") {
    return Number.isFinite(eligible)
      ? { available: true, numerator: eligible, denominator: null, value: eligible }
      : { available: false, numerator: null, denominator: null, value: null };
  }

  if (metricKey === "participating_voters") {
    return Number.isFinite(participating)
      ? { available: true, numerator: participating, denominator: null, value: participating }
      : { available: false, numerator: null, denominator: null, value: null };
  }

  if (metricKey === "turnout_pct") {
    if (Number.isFinite(eligible) && eligible > 0 && Number.isFinite(participating)) {
      return {
        available: true,
        numerator: participating,
        denominator: eligible,
        value: (participating / eligible) * 100,
      };
    }
    if (Number.isFinite(turnout)) {
      return { available: true, numerator: null, denominator: null, value: turnout };
    }
  }

  return { available: false, numerator: null, denominator: null, value: null };
}

export function getMetricValueFromStats(statsRow, metricKey) {
  const metric = getMetricNumeratorAndDenominator(statsRow, metricKey);
  return { available: metric.available, value: metric.value };
}

function buildValuePointForParty(resultRow, selectedEntities, valueMode) {
  if (!resultRow) return { available: false, value: null, selectedVotes: null, totalVotes: null };
  const selectedVotes = getSelectedVotes(resultRow, selectedEntities);
  const totalVotes = getTotalResultVotes(resultRow);
  if (!Number.isFinite(selectedVotes)) {
    return { available: false, value: null, selectedVotes: null, totalVotes };
  }
  if (valueMode === "absolute") {
    return { available: true, value: selectedVotes, selectedVotes, totalVotes };
  }
  if (!Number.isFinite(totalVotes) || totalVotes <= 0) {
    return { available: false, value: null, selectedVotes, totalVotes };
  }
  return {
    available: true,
    value: (selectedVotes / totalVotes) * 100,
    selectedVotes,
    totalVotes,
  };
}

function buildValuePointForMetric(statsRow, metricKey) {
  const metric = getMetricValueFromStats(statsRow, metricKey);
  return {
    available: metric.available,
    value: metric.value,
    selectedVotes: null,
    totalVotes: null,
  };
}

export function classifyTrendDelta(delta) {
  if (!Number.isFinite(delta)) return "na";
  if (delta > 0) return "+";
  if (delta < 0) return "-";
  return "0";
}

export function classifyTrendState(valuePoints) {
  const points = Array.isArray(valuePoints) ? valuePoints : [];
  if (points.length < 2) {
    return {
      kind: "no_data",
      key: "N/A",
      label: "Nedostatečná data",
      deltas: [],
      netDelta: null,
    };
  }

  const hasMissing = points.some((point) => !point || point.available !== true || !Number.isFinite(point.value));
  if (hasMissing) {
    return {
      kind: "no_data",
      key: "N/A",
      label: "Nedostatečná data",
      deltas: [],
      netDelta: null,
    };
  }

  const deltas = [];
  for (let index = 1; index < points.length; index += 1) {
    deltas.push(Number(points[index].value) - Number(points[index - 1].value));
  }
  const signs = deltas.map(classifyTrendDelta);
  const netDelta = Number(points[points.length - 1].value) - Number(points[0].value);

  if (points.length === 2) {
    const sign = signs[0];
    return {
      kind: sign === "+" ? "positive" : sign === "-" ? "negative" : "neutral",
      key: sign,
      label: sign === "+" ? "Růst" : sign === "-" ? "Pokles" : "Beze změny",
      deltas,
      netDelta,
      signs,
    };
  }

  const exactState = `(${signs.join(",")})`;
  if (signs.includes("0")) {
    return {
      kind: "flat_transition",
      key: exactState,
      label: "Obsahuje stagnaci",
      deltas,
      netDelta,
      signs,
    };
  }

  const joined = signs.join(",");
  const kind =
    joined === "+,+" ? "up_up" : joined === "-,-" ? "down_down" : joined === "-,+" ? "down_up" : "up_down";
  const label =
    kind === "up_up"
      ? "Růst v obou krocích"
      : kind === "down_down"
        ? "Pokles v obou krocích"
        : kind === "down_up"
          ? "Nejprve pokles, pak růst"
          : "Nejprve růst, pak pokles";
  return {
    kind,
    key: exactState,
    label,
    deltas,
    netDelta,
    signs,
  };
}

export function buildTrendRows(appliedQuery, bundlesByElectionId) {
  const years = Array.isArray(appliedQuery && appliedQuery.years) ? appliedQuery.years : [];
  if (years.length < 2) {
    return {
      rows: [],
      mismatchSummary: { newestMissingHistory: 0, olderOnlyDistricts: [] },
    };
  }

  const newestYear = years[years.length - 1];
  const newestBundle = bundlesByElectionId && newestBundleById(bundlesByElectionId, newestYear.election.id);
  if (!newestBundle) {
    return {
      rows: [],
      mismatchSummary: { newestMissingHistory: 0, olderOnlyDistricts: [] },
    };
  }

  const resultsMaps = new Map();
  const statsMaps = new Map();
  years.forEach((entry) => {
    const bundle = newestBundleById(bundlesByElectionId, entry.election.id);
    resultsMaps.set(entry.election.id, makeResultsMap(bundle));
    statsMaps.set(entry.election.id, makeStatsMap(bundle));
  });

  const newestFeatures = Array.isArray(newestBundle.districtsGeoJson && newestBundle.districtsGeoJson.features)
    ? newestBundle.districtsGeoJson.features
    : [];
  const rows = newestFeatures.map((feature) => {
    const okrsek = String(feature && feature.properties ? feature.properties.okrsek || "" : "").trim();
    const points = years.map((entry) => {
      const electionId = entry.election.id;
      if (appliedQuery.analysisMode === METRIC_ANALYSIS_MODE) {
        const statsRow = statsMaps.get(electionId).get(okrsek) || null;
        return {
          year: entry.year,
          electionId,
          ...buildValuePointForMetric(statsRow, appliedQuery.metricKey),
          statsRow,
          resultRow: resultsMaps.get(electionId).get(okrsek) || null,
        };
      }
      const resultRow = resultsMaps.get(electionId).get(okrsek) || null;
      return {
        year: entry.year,
        electionId,
        ...buildValuePointForParty(
          resultRow,
          appliedQuery.entitySelections && appliedQuery.entitySelections[electionId],
          appliedQuery.valueMode
        ),
        statsRow: statsMaps.get(electionId).get(okrsek) || null,
        resultRow,
      };
    });

    const state = classifyTrendState(points);
    const supportStats = statsMaps.get(newestYear.election.id).get(okrsek) || null;
    const olderMissing = points.slice(0, -1).some((point) => point.available !== true);

    return {
      precinctId: okrsek,
      precinctCode: getOkrsekTail(okrsek),
      districtLabel: `Okrsek ${getOkrsekTail(okrsek)}`,
      points,
      state,
      netDelta: state.netDelta,
      supportStats,
      geometryFeature: feature,
      isMismatch: olderMissing,
    };
  });

  const newestSet = new Set(rows.map((row) => row.precinctId));
  const olderOnlyDistricts = [];
  years.slice(0, -1).forEach((entry) => {
    const bundle = newestBundleById(bundlesByElectionId, entry.election.id);
    const ids = new Set();
    (Array.isArray(bundle && bundle.results) ? bundle.results : []).forEach((row) => {
      const okrsek = String(row && row.okrsek ? row.okrsek : "").trim();
      if (!okrsek || newestSet.has(okrsek) || ids.has(okrsek)) return;
      ids.add(okrsek);
      olderOnlyDistricts.push({
        precinctId: okrsek,
        precinctCode: getOkrsekTail(okrsek),
        year: entry.year,
      });
    });
  });

  return {
    rows,
    mismatchSummary: {
      newestMissingHistory: rows.filter((row) => row.state.kind === "no_data").length,
      olderOnlyDistricts,
    },
  };
}

function newestBundleById(bundlesByElectionId, electionId) {
  return bundlesByElectionId && bundlesByElectionId[electionId] ? bundlesByElectionId[electionId] : null;
}

function aggregateBundleForYear(appliedQuery, bundle, yearEntry) {
  if (!bundle) {
    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: false,
      value: null,
    };
  }

  if (appliedQuery.analysisMode === METRIC_ANALYSIS_MODE) {
    if (!Array.isArray(bundle.stats) || bundle.stats.length === 0) {
      return {
        year: yearEntry.year,
        electionId: yearEntry.election.id,
        available: false,
        value: null,
      };
    }

    if (appliedQuery.metricKey === "turnout_pct") {
      let numerator = 0;
      let denominator = 0;
      let hasRatio = false;
      let fallbackSum = 0;
      let fallbackCount = 0;

      bundle.stats.forEach((statsRow) => {
        const metric = getMetricNumeratorAndDenominator(statsRow, appliedQuery.metricKey);
        if (!metric.available) return;
        if (Number.isFinite(metric.numerator) && Number.isFinite(metric.denominator) && metric.denominator > 0) {
          numerator += metric.numerator;
          denominator += metric.denominator;
          hasRatio = true;
          return;
        }
        if (Number.isFinite(metric.value)) {
          fallbackSum += metric.value;
          fallbackCount += 1;
        }
      });

      if (hasRatio && denominator > 0) {
        return {
          year: yearEntry.year,
          electionId: yearEntry.election.id,
          available: true,
          value: (numerator / denominator) * 100,
        };
      }

      if (fallbackCount > 0) {
        return {
          year: yearEntry.year,
          electionId: yearEntry.election.id,
          available: true,
          value: fallbackSum / fallbackCount,
        };
      }

      return {
        year: yearEntry.year,
        electionId: yearEntry.election.id,
        available: false,
        value: null,
      };
    }

    let total = 0;
    let foundAny = false;
    bundle.stats.forEach((statsRow) => {
      const metric = getMetricNumeratorAndDenominator(statsRow, appliedQuery.metricKey);
      if (!metric.available || !Number.isFinite(metric.value)) return;
      total += metric.value;
      foundAny = true;
    });

    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: foundAny,
      value: foundAny ? total : null,
    };
  }

  if (!Array.isArray(bundle.results) || bundle.results.length === 0) {
    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: false,
      value: null,
    };
  }

  const selectedEntities =
    appliedQuery.entitySelections && appliedQuery.entitySelections[yearEntry.election.id]
      ? appliedQuery.entitySelections[yearEntry.election.id]
      : [];

  let selectedVotesTotal = 0;
  let totalVotesTotal = 0;
  let foundSelected = false;
  let foundTotalVotes = false;

  bundle.results.forEach((resultRow) => {
    const selectedVotes = getSelectedVotes(resultRow, selectedEntities);
    const totalVotes = getTotalResultVotes(resultRow);

    if (Number.isFinite(selectedVotes)) {
      selectedVotesTotal += selectedVotes;
      foundSelected = true;
    }
    if (Number.isFinite(totalVotes)) {
      totalVotesTotal += totalVotes;
      foundTotalVotes = true;
    }
  });

  if (!foundSelected) {
    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: false,
      value: null,
    };
  }

  if (appliedQuery.valueMode === "absolute") {
    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: true,
      value: selectedVotesTotal,
    };
  }

  if (!foundTotalVotes || totalVotesTotal <= 0) {
    return {
      year: yearEntry.year,
      electionId: yearEntry.election.id,
      available: false,
      value: null,
    };
  }

  return {
    year: yearEntry.year,
    electionId: yearEntry.election.id,
    available: true,
    value: (selectedVotesTotal / totalVotesTotal) * 100,
  };
}

function parsePrecinctCodeForSort(value) {
  const raw = String(value || "").trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : Number.POSITIVE_INFINITY;
}

export function filterAndSortTrendRows(rows, options) {
  const {
    sortKey = "district_code",
    stateFilter = "all",
    limitKey = "all",
    selectedYearsCount = 2,
  } = options || {};

  let filtered = Array.isArray(rows) ? rows.slice() : [];
  filtered = filtered.filter((row) => {
    if (stateFilter === "all") return true;
    if (stateFilter === "mismatch_only") return row.state.kind === "no_data";
    if (selectedYearsCount === 2) {
      if (stateFilter === "positive_only") return row.state.kind === "positive";
      if (stateFilter === "neutral_only") return row.state.kind === "neutral";
      if (stateFilter === "negative_only") return row.state.kind === "negative";
      return true;
    }
    if (stateFilter === "mixed_only") return row.state.kind === "down_up" || row.state.kind === "up_down";
    if (stateFilter === "pure_only") return row.state.kind === "up_up" || row.state.kind === "down_down";
    if (stateFilter === "flat_only") return row.state.kind === "flat_transition";
    return true;
  });

  filtered.sort((a, b) => {
    if (sortKey === "biggest_increase") {
      const av = Number.isFinite(a.netDelta) ? a.netDelta : Number.NEGATIVE_INFINITY;
      const bv = Number.isFinite(b.netDelta) ? b.netDelta : Number.NEGATIVE_INFINITY;
      if (bv !== av) return bv - av;
    } else if (sortKey === "biggest_decrease") {
      const av = Number.isFinite(a.netDelta) ? a.netDelta : Number.POSITIVE_INFINITY;
      const bv = Number.isFinite(b.netDelta) ? b.netDelta : Number.POSITIVE_INFINITY;
      if (av !== bv) return av - bv;
    } else if (sortKey === "biggest_absolute_change") {
      const av = Number.isFinite(a.netDelta) ? Math.abs(a.netDelta) : Number.NEGATIVE_INFINITY;
      const bv = Number.isFinite(b.netDelta) ? Math.abs(b.netDelta) : Number.NEGATIVE_INFINITY;
      if (bv !== av) return bv - av;
    }

    const byCode = parsePrecinctCodeForSort(a.precinctCode) - parsePrecinctCodeForSort(b.precinctCode);
    if (byCode !== 0) return byCode;
    return String(a.precinctId || "").localeCompare(String(b.precinctId || ""), "cs");
  });

  if (limitKey === "top_10") return filtered.slice(0, 10);
  if (limitKey === "top_25") return filtered.slice(0, 25);
  if (limitKey === "top_50") return filtered.slice(0, 50);
  return filtered;
}

export function buildAreaBaselinePoints(appliedQuery, bundlesByElectionId) {
  const years = Array.isArray(appliedQuery && appliedQuery.years) ? appliedQuery.years : [];
  return years.map((yearEntry) =>
    aggregateBundleForYear(
      appliedQuery,
      newestBundleById(bundlesByElectionId, yearEntry.election.id),
      yearEntry
    )
  );
}

export function normalizeTrendsQuerySnapshot(queryLike) {
  const family = String(queryLike && queryLike.family ? queryLike.family : "");
  const yearsSource = Array.isArray(queryLike && queryLike.years)
    ? queryLike.years.map((entry) => Number(entry && entry.year))
    : Array.isArray(queryLike && queryLike.selectedYears)
      ? queryLike.selectedYears.map((year) => Number(year))
      : [];
  const years = yearsSource.filter((year) => Number.isInteger(year)).sort((a, b) => a - b);

  let entitySelections = {};
  if (queryLike && queryLike.entitySelections && typeof queryLike.entitySelections === "object") {
    entitySelections = Object.keys(queryLike.entitySelections)
      .sort((a, b) => a.localeCompare(b, "cs"))
      .reduce((acc, electionId) => {
        const values = Array.isArray(queryLike.entitySelections[electionId])
          ? queryLike.entitySelections[electionId]
          : [];
        acc[electionId] = values
          .map((item) => String(item && item.key ? item.key : item || "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "cs"));
        return acc;
      }, {});
  }

  return {
    family,
    years,
    analysisMode: queryLike && queryLike.analysisMode === METRIC_ANALYSIS_MODE ? METRIC_ANALYSIS_MODE : PARTY_ANALYSIS_MODE,
    valueMode: queryLike && queryLike.valueMode === "absolute" ? "absolute" : "percentage",
    metricKey:
      queryLike && typeof queryLike.metricKey === "string" && queryLike.metricKey.trim()
        ? queryLike.metricKey.trim()
        : TRENDS_METRICS[0].key,
    entitySelections,
  };
}

export function formatTrendDeltaValue(delta, config) {
  if (!Number.isFinite(delta)) return "N/A";

  const isPercentage =
    (config && config.analysisMode === METRIC_ANALYSIS_MODE && config.metricKey === "turnout_pct") ||
    (config && config.analysisMode === PARTY_ANALYSIS_MODE && config.valueMode === "percentage");

  if (isPercentage) {
    const sign = delta > 0 ? "+" : delta < 0 ? "-" : "";
    const amount = Math.abs(delta).toLocaleString("cs-CZ", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return `${sign}${amount} p. b.`;
  }

  if (delta === 0) return "0";
  return `${delta > 0 ? "+" : "-"}${formatIntLike(Math.abs(delta))}`;
}

function formatIntLike(value) {
  return Number(value).toLocaleString("cs-CZ", {
    maximumFractionDigits: 0,
  });
}

export function isDraftQueryValid(draftQuery, matrix) {
  const applied = buildAppliedQueryFromDraft(draftQuery, matrix);
  if (applied.years.length < 2 || applied.years.length > 3) return false;
  if (applied.analysisMode === METRIC_ANALYSIS_MODE) return Boolean(applied.metricKey);
  return applied.years.every((entry) => {
    const selected = applied.entitySelections && applied.entitySelections[entry.election.id];
    return Array.isArray(selected) && selected.length > 0;
  });
}

export function getStatePresentation(stateKind) {
  switch (stateKind) {
    case "positive":
      return { fill: "#4ade80", stroke: "#166534" };
    case "negative":
      return { fill: "#f97316", stroke: "#9a3412" };
    case "neutral":
      return { fill: "#60a5fa", stroke: "#1d4ed8" };
    case "up_up":
      return { fill: "#22c55e", stroke: "#166534" };
    case "down_down":
      return { fill: "#ef4444", stroke: "#991b1b" };
    case "down_up":
      return { fill: "#86efac", stroke: "#166534" };
    case "up_down":
      return { fill: "#fb923c", stroke: "#9a3412" };
    case "flat_transition":
      return { fill: "#93c5fd", stroke: "#1d4ed8" };
    case "no_data":
    default:
      return { fill: "#e5e7eb", stroke: "#64748b" };
  }
}
